import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import classNames from "classnames";

import { Icon } from "flavours/glitch/components/icon";

const storageKey = id => `mastodon-accordion:${id}`;

const readOpen = (id, fallback) => {
  try {
    const value = window.sessionStorage.getItem(storageKey(id));
    return value === null ? fallback : value === "1";
  } catch {
    return fallback;
  }
};

const writeOpen = (id, open) => {
  try {
    window.sessionStorage.setItem(storageKey(id), open ? "1" : "0");
  } catch {
    // storage may be unavailable, the section simply does not remember its state
  }
};

const ANIMATION_MS = 260;

// One collapsible section ("Ziehharmonika"). The body is only mounted while the
// section is open or sliding shut, so embedded pages load their data lazily.
// `forceOpen` opens the section and scrolls to it, whatever state was remembered,
// for links that point at one section.
const Accordion = ({ id, icon, title, defaultOpen = false, forceOpen = false, children }) => {
  const [initiallyOpen] = useState(() => forceOpen || readOpen(id, defaultOpen));
  const [expanded, setExpanded] = useState(initiallyOpen);
  const [mounted, setMounted] = useState(initiallyOpen);
  const timer = useRef(null);
  const node = useRef(null);

  useEffect(() => () => {
    clearTimeout(timer.current);
  }, []);

  // A link to this section while the page is already open: open it during render.
  const [wasForced, setWasForced] = useState(forceOpen);
  if (forceOpen !== wasForced) {
    setWasForced(forceOpen);
    if (forceOpen) {
      setMounted(true);
      setExpanded(true);
    }
  }

  useEffect(() => {
    if (!forceOpen) {
      return;
    }

    clearTimeout(timer.current);
    writeOpen(id, true);
    // After the other sections have settled, so the position is final.
    timer.current = setTimeout(() => node.current?.scrollIntoView({ block: "start" }), ANIMATION_MS);
  }, [forceOpen, id]);

  const handleClick = () => {
    clearTimeout(timer.current);
    writeOpen(id, !expanded);

    if (expanded) {
      setExpanded(false);
      timer.current = setTimeout(() => setMounted(false), ANIMATION_MS);
    } else {
      setMounted(true);
      // Two frames, so the collapsed state is painted before the transition starts.
      requestAnimationFrame(() => requestAnimationFrame(() => setExpanded(true)));
    }
  };

  return (
    <section ref={node} className={classNames("accordion", { "accordion--open": expanded })}>
      <button type='button' className='accordion__header' aria-expanded={expanded} onClick={handleClick}>
        {icon && <Icon id={icon} className='accordion__icon' />}
        <span className='accordion__title'>{title}</span>
        <Icon id='caret-down' className='accordion__caret' />
      </button>

      <div className='accordion__panel'>
        <div className='accordion__body'>{mounted && children}</div>
      </div>
    </section>
  );
};

Accordion.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  forceOpen: PropTypes.bool,
  children: PropTypes.node,
};

export default Accordion;
